import React, { useState, useEffect } from "react";
import { planService } from "../../services/api/planService";
import { toast } from "react-toastify";
import Select from "../common/Select";

const PlanModal = ({ show, onHide, modalMode = "add", planData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    billingCycle: "month",
    price: "",
    currency: "USD",
  });

   useEffect(() => {
    if (modalMode === "edit" && planData) {
      setFormData({
        name: planData.name || planData.planName || "",
        description: planData.description || "",
        billingCycle: planData.billingCycle || "month",
        price: planData.price?.toString().replace("$", "") || "",
        currency: planData.currency || "USD",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        billingCycle: "month",
        price: "",
        currency: "USD",
      });
    }
  }, [modalMode, planData, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Plan name is required");
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("Valid price is required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        billingCycle: formData.billingCycle,
        price: parseFloat(formData.price),
        currency: formData.currency,
      };

      let response;
      if (modalMode === "edit" && planData) {
        response = await planService.update(
          planData.id || planData._id,
          payload
        );
      } else {
        response = await planService.create(payload);
      }

      if (response?._id || response?.message) {
        toast.success(
          modalMode === "edit"
            ? "Plan updated successfully"
            : "Plan created successfully"
        );
        onSuccess?.();
        onHide();
      } else {
        toast.error(response?.message || "Failed to save plan");
      }
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error("An error occurred while saving the plan");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[1100]"
      onClick={onHide}
    >
      <div className="relative p-[1px] w-full max-w-[500px] min-w-[300px] rounded-[12px] bg-gradient-to-br from-[#0058E8] to-[#00318200]">
        <div
          className="bg-white rounded-[12px] shadow-lg p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-xl font-semibold text-text-dark mb-5">
            {modalMode === "add" ? "Create New Plan" : "Edit Plan"}
          </h2>

          <div className="flex flex-col gap-4">
            <div>
              <label className="text-sm text-text-secondary mb-1 block">
                Plan Name *
              </label>
              <input
                placeholder="e.g., Premium Plan"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-border-light rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="text-sm text-text-secondary mb-1 block">
                Description
              </label>
              <textarea
                placeholder="Plan description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-border-light rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-text-secondary mb-1 block">
                  Price *
                </label>
                <input
                  placeholder="99.99"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-border-light rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="text-sm text-text-secondary mb-1 block">
                  Currency
                </label>
                <Select
                  value={formData.currency}
                  onChange={(e) => setFormData((prev) => ({ ...prev, currency: e.target.value }))}
                  wrapperClassName=""
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm text-text-secondary mb-1 block">
                Billing Cycle
              </label>
              <Select
                value={formData.billingCycle}
                onChange={(e) => setFormData((prev) => ({ ...prev, billingCycle: e.target.value }))}
                wrapperClassName=""
              >
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </Select>
            </div>

            <hr className="w-full border-t border-gray-200 mt-3" />

            <div className="flex gap-3 mt-2 justify-end">
              <button
                className="text-sm font-semibold text-text-dark bg-white border border-gray-200 py-3 px-4 rounded-lg"
                onClick={onHide}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="text-sm font-semibold bg-primary text-white py-3 px-4 rounded-lg disabled:opacity-50"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Saving..." : modalMode === "edit" ? "Update Plan" : "Create Plan"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanModal;
